import clsx from 'clsx';
import React, { useState, useEffect, useRef } from 'react';

interface ChipListProps {
  value: string[];
  onChange: (chips: string[]) => void;
  disabled?: boolean;
  dataCy?: string;
}

const ChipList: React.FC<ChipListProps> = ({ value, onChange, disabled }) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue('');
    if (inputRef.current) {
      inputRef.current.textContent = '';
    }
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && inputValue.trim() !== '') {
      e.preventDefault();
      onChange([...value, inputValue.trim()]);
      setInputValue('');
      if (inputRef.current) {
        inputRef.current.textContent = '';
      }
    } else if (e.key === 'Backspace' && inputValue === '') {
      if (value.length > 0) {
        e.preventDefault();
        const newChips = value.slice(0, -1);
        onChange(newChips);
      }
    }
  };

  const handleRemoveChip = (index: number) => {
    const newChips = value.filter((_, i) => i !== index);
    onChange(newChips);
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    setInputValue(e.currentTarget.textContent || '');
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // Prevent the blur event from firing when the delete button is clicked
    e.preventDefault();
  };

  return (
    <div
      className={clsx(
        'border rounded flex items-center text-xs text-white',
        disabled ? 'border-shadow-200 bg-ui-secondary !text-ui-helper' : 'bg-white border-ui-200'
      )}
    >
      <div
        className={clsx(
          'flex flex-wrap items-center px-2 py-1 w-full',
          isFocused && value.length > 1 ? 'h-full' : 'h-8'
        )}
      >
        {isFocused
          ? value.map((chip, index) => (
              <React.Fragment key={index}>
                <div
                  key={index}
                  className={clsx(
                    'border rounded-full mx-1 flex items-center px-2 py-1 text-xs text-white bg-midnight-200'
                  )}
                >
                  {chip}
                  <button
                    onMouseDown={handleMouseDown} // Prevents the input from losing focus
                    onClick={() => handleRemoveChip(index)}
                    className='ml-2 text-white'
                    disabled={disabled}
                  >
                    &times;
                  </button>
                </div>
                {index < value.length - 1 && (
                  <span className='m-1 text-[10px] text-ui-helper'>OR</span>
                )}
              </React.Fragment>
            ))
          : value.length > 0 && (
              <div className='flex items-center'>
                <div
                  className={clsx(
                    'border rounded-full mx-1 flex items-center px-2 py-1 text-xs text-white bg-midnight-200'
                  )}
                >
                  {value[0]}
                  <button
                    onMouseDown={handleMouseDown} // Prevents the input from losing focus
                    onClick={() => handleRemoveChip(0)}
                    className='ml-2 text-white'
                    disabled={disabled}
                  >
                    &times;
                  </button>
                </div>
                {value.length > 1 && (
                  <span className='m-1 text-xs text-ui'>+ {value.length - 1}</span>
                )}
              </div>
            )}
        <div
          className={clsx(
            'flex-1 border-none focus:outline-none resize-none w-10 text-ui min-w[30px]',
            disabled ? 'pointer-events-none' : ''
          )}
          contentEditable={!disabled}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          ref={inputRef}
          suppressContentEditableWarning
        ></div>
      </div>
    </div>
  );
};

export default ChipList;
